import _isFunction from "lodash/isFunction";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import { openGenericModal, openProductCardModal } from "web/redux/ducks/send-to-app-modal";
import store from "web/redux/store";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import userProfiler from "web/script/modules/userprofiler";
import browser from "web/script/utils/browser";
import globalEvents from "web/script/utils/global-events";
import history from "web/script/utils/history";
import navigate from "web/script/utils/navigate";
import storage from "web/script/utils/storage";
import url from "web/script/utils/url";

interface UseProductClickArgs {
    id?: string;
    productUrl?: string;
    productImageUrl?: string;
    inStock?: boolean;
    shouldRedirect?: boolean;
    overlayEnabled?: boolean;
    productUid?: string;
    productSlug?: string;
    reason?: string;
    linkId?: string;
    sendAnalytics?: () => void;
    href?: string;
    isLeadLink?: boolean;
    openInNewTab?: boolean;
    leadURL?: string;
}

interface UseProductClickResponse {
    onClick: (event) => any;
}

function extractEventCount(state): any {
    return {
        eventCountBlock: state.eventCountTrackerReducer.shouldBlockAll,
        semiHardBlock: state.eventCountTrackerReducer.shouldBlockSemiHard,
    };
}

function showValidWebToAppCampaign(id, productUrl, productImageUrl, inStock, leadURL): boolean {
    const { eventCountBlock, semiHardBlock } = extractEventCount(store.getState());
    const shouldSemiBlock =
        environment.getFeature("mg_semi_hard_block_feeds_show_more") &&
        semiHardBlock &&
        environment.getFeature("mg_feeds_campaigns");

    const eventCounter = storage.get("eventCounter", false, true);
    const stopAllFurtherBlocks =
        environment.getFeature("mg_second_interaction_product_card_soft_block") &&
        eventCounter.hasBeenDismissed;

    if (
        browser.ios &&
        !userProfiler.isLoggedIn() &&
        eventCountBlock &&
        environment.get("pageType") === "feed"
    ) {
        store.dispatch(
            openGenericModal({
                modalType: "event_counter",
                product: {
                    productUrl: productUrl as string,
                    productImageUrl: productImageUrl as string,
                    inStock: inStock as boolean,
                },
                paywalledClassName: "product-card",
            })
        );
        return true;
    }

    const productFeedsCapture = environment.getFeature("mg_feeds_product_card_android_block");
    const hasBeenBlockedOnce = !!storage.get("hasBeenBlockedProductCard", false, true);

    const blockRelatedIos =
        environment.getFeature("mg_block_is_pdp_related_product_card") &&
        environment.getFeature("mg_block_is_pdp_related_product_card") !== "no_block" &&
        storage.get("blockHasBeenDismissed", false, true);
    const shouldBlockGeneralFeed = environment.getFeature("mg_feeds_campaigns");
    const shouldBlockRelatedFeed =
        blockRelatedIos ||
        (environment.getFeature("mg_android_block_is_pdp_related_product_card") &&
            environment.getFeature("mg_android_block_is_pdp_related_product_card") !== "no_block");
    const shouldBlockProductFeeds =
        productFeedsCapture &&
        (productFeedsCapture === "hard_block" ||
            (productFeedsCapture === "soft_block" && !hasBeenBlockedOnce));
    const firstTimeSoftBlock =
        !environment.getFeature("mg_soft_block_product_card_feeds") ||
        (environment.getFeature("mg_soft_block_product_card_feeds") &&
            !storage.get("blockHasBeenDismissed", false, true));

    const continueToRetailerSoftBlock =
        environment.getFeature("mg_continue_to_retailer_block_on_feeds") &&
        storage.get("blockHasBeenDismissed", false, true);
    const showSignupSoftBlock =
        environment.getFeature("mg_second_block_sign_up_feeds") &&
        storage.get("blockHasBeenDismissed", false, true);

    const web2AppBlock =
        environment.getFeature("mg_feeds_campaigns") &&
        (browser.ios ||
            (browser.android &&
                environment.getFeature("mg_soft_block_product_card_feeds_android")));

    if (
        web2AppBlock &&
        (!userProfiler.isLoggedIn() || environment.getFeature("mg_block_logged_in_users")) &&
        !stopAllFurtherBlocks &&
        (firstTimeSoftBlock || continueToRetailerSoftBlock) &&
        (shouldBlockGeneralFeed || shouldBlockRelatedFeed || shouldSemiBlock)
    ) {
        store.dispatch(
            openProductCardModal({
                productUrl: productUrl as string,
                productImageUrl: productImageUrl as string,
                inStock: inStock as boolean,
                leadURL: leadURL,
            })
        );
        return true;
    }
    if (showSignupSoftBlock && !userProfiler.isLoggedIn() && web2AppBlock) {
        const captureType = "feeds_product_card";
        store.dispatch(
            openCustomerCaptureOverlay({
                productId: id,
                captureType,
                next: productUrl,
            })
        );
        return true;
    }

    if (
        browser.android &&
        !userProfiler.isLoggedIn() &&
        (shouldBlockRelatedFeed || shouldBlockProductFeeds)
    ) {
        if (shouldBlockProductFeeds) {
            storage.set("hasBeenBlockedProductCard", "true", true);
        }
        const captureType = shouldBlockProductFeeds ? "feeds_product_card" : "related_product";
        store.dispatch(
            openCustomerCaptureOverlay({
                productId: id,
                captureType,
                next: productUrl,
            })
        );
        return true;
    }

    return false;
}

/**
 * Returns true if a new tab should be used for the event
 */
function _isNewTab(event): void {
    return (browser.mac && event.metaKey) || (!browser.mac && event.ctrlKey) || event.which === 2;
}

const DEFAULT_PRODUCT_CLICK_ARGS = {
    inStock: false,
    overlayEnabled: false,
};

// useProductClick is a hook counterpart for the legacy product-click-mixin typically used in Baustein components
// use with caution as code may diverge between the two versions as long as both exist
export function useProductClick(props: UseProductClickArgs): UseProductClickResponse {
    const args = { ...DEFAULT_PRODUCT_CLICK_ARGS, ...props };
    const {
        id,
        productUrl,
        productImageUrl,
        inStock,
        shouldRedirect,
        overlayEnabled,
        productSlug,
        reason,
        linkId,
        sendAnalytics,
        href,
        isLeadLink,
        openInNewTab,
        leadURL,
    } = args;

    function _redirectWithReason(event, newTab = false): void {
        let parsed = url.parse(href as string);

        event.preventDefault();

        if (reason) {
            parsed.searchParams.set("reason", reason);
        }

        if (newTab) {
            globals.window.open(url.unparse(parsed), "_blank");
            return;
        }

        navigate(url.unparse(parsed));
    }

    /**
     * Product click event handler that will either redirect or open a new tab to product page.
     */
    function _onClickRedirect(event): void {
        if (event.target.classList.contains("show-more-button")) {
            return;
        }

        let newTab = _isNewTab(event) as any;

        globalEvents.trigger("product-card-selected", event);
        _redirectWithReason(event, newTab);
    }

    /**
     * Product click event handler that will either open an overlay, new tab or redirect to product page.
     */
    function _onClick(event): void {
        if (event.target.classList.contains("show-more-button")) {
            return;
        }

        event.preventDefault();
        if (_isFunction(sendAnalytics)) {
            sendAnalytics();
        }

        const isOverlayEnabled =
            overlayEnabled &&
            productSlug &&
            browser.supportsHistory &&
            !event.metaKey &&
            !browser.ios &&
            !browser.android &&
            browser.getViewport() === browser.VIEWPORTS.DESKTOP;

        globalEvents.trigger("product-card-selected", event);

        if (showValidWebToAppCampaign(id, productUrl, productImageUrl, inStock, leadURL)) {
            return;
        }

        // if the overlay isn't enabled, and the target is to open a new tab, then bail out here
        if (!isOverlayEnabled && openInNewTab) {
            // the lead-link click handler also calls `preventDefault` and calls window.open. we don't
            // want to to it twice.
            if (!isLeadLink) {
                globals.window.open(href);
            }
            return;
        }

        let newTab = _isNewTab(event) as any;

        if (
            !isOverlayEnabled ||
            newTab ||
            !reason ||
            browser.getViewport() === browser.VIEWPORTS.MOBILE
        ) {
            _redirectWithReason(event, newTab);
        } else {
            let queryParams = {
                reason: reason,
                link_id: linkId,
            };

            let url = new URL(href as string, window.location.href);
            for (let [k, v] of Object.entries(queryParams)) {
                url.searchParams.set(k, v as string);
            }

            history.pushState(url);

            globalEvents.trigger("open-overlay", {
                url: href,
                queryParams,
            });
        }
    }

    return { onClick: shouldRedirect ? _onClickRedirect : _onClick };
}
